import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { formatMinorToMajorAmount } from '../../../utils/currency';
import { useGetPricesByIdsHandler } from '../dataHandlers/getPricesByIdsHandler';
import { map } from 'lodash';

export default function SubscriptionItems({
  errors,
  items = [],
  readOnly = false,
  onLimitChange,
  onItemMetadataChange,
}) {
  const { prices, isLoading } = useGetPricesByIdsHandler({
    ids: map(items, 'stripe_price_id'),
  });

  useEffect(() => {
    if (prices.length === 0) {
      return;
    }

    items.forEach((item, index) => {
      const matchedPrice = prices.find((price) => price.stripe_price_id === item.stripe_price_id);
      const metadataChanged =
        item?.pricing_model !== (matchedPrice?.pricing_model || null) ||
        item?.meter_code !== (matchedPrice?.meter_code || null);

      if (matchedPrice && metadataChanged) {
        onItemMetadataChange?.(index, matchedPrice);
      }
    });
  }, [items, onItemMetadataChange, prices]);

  const getLimitError = (index) => {
    return errors?.items?.[index]?.limit?.message || '';
  };

  if (isLoading || prices.length === 0) {
    return null;
  }
  return (
    <Box sx={{ width: '100%' }}>
      {isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Loading prices...
        </Typography>
      )}
      <Stack spacing={1.5}>
        {items
          .map((item) => prices.find((price) => price.stripe_price_id === item.stripe_price_id) || {})
          .map((row, index) => {
            const errorMessage = getLimitError(index);
            const limitHelperText = errorMessage || '';
            const pricingModel = row.pricing_model;
            const isUsageBased = pricingModel === 'metered';
            const itemLimit = items[index]?.limit ?? 0;
            const showInfiniteLimit = isUsageBased;

            return (
              <Card key={row.stripe_price_id || row.name} variant="outlined" sx={{ width: '100%' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Stripe Price ID
                      </Typography>
                      <Typography variant="body2">{row.stripe_price_id || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body2">{row.name || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="caption" color="text.secondary">
                        Pricing Model
                      </Typography>
                      <Typography variant="body2">{row.pricing_model || '—'}</Typography>
                    </Grid>
                    {row.pricing_model !== 'licensed' && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Billing Schema
                        </Typography>
                        <Typography variant="body2">{row.billing_schema || '—'}</Typography>
                      </Grid>
                    )}
                    {row.pricing_model !== 'licensed' && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Tiers Mode
                        </Typography>
                        <Typography variant="body2">{row.tiers_mode || '—'}</Typography>
                      </Grid>
                    )}
                    {row.pricing_model !== 'licensed' && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Meter Code
                        </Typography>
                        <Typography variant="body2">{row.meter_code || '—'}</Typography>
                      </Grid>
                    )}
                    {(row.pricing_model === 'licensed' || row.billing_schema === 'per_unit') && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Unit Amount
                        </Typography>
                        <Typography variant="body2">
                          {formatMinorToMajorAmount(row.unit_amount, { emptyValue: '—' })}
                        </Typography>
                      </Grid>
                    )}
                    {row.billing_schema === 'tiered' && (
                      <Grid size={12}>
                        <Typography variant="caption" color="text.secondary">
                          Tiers
                        </Typography>
                        {row.tiers?.length > 0 ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.75 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>From</TableCell>
                                  <TableCell>Up to</TableCell>
                                  <TableCell>Per unit</TableCell>
                                  <TableCell>Flat fee</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {row.tiers.map((tier, index) => (
                                  <TableRow key={`${row.stripe_price_id || row.name}-tier-${index}`}>
                                    <TableCell>
                                      {tier?.starting_from !== null && tier?.starting_from !== undefined
                                        ? String(tier.starting_from)
                                        : index === 0
                                          ? '0'
                                          : String(index)}
                                    </TableCell>
                                    <TableCell>
                                      {tier?.up_to === 'inf' || tier?.up_to === null || tier?.up_to === undefined
                                        ? 'Unlimited'
                                        : String(tier.up_to)}
                                    </TableCell>
                                    <TableCell>
                                      {formatMinorToMajorAmount(tier?.unit_amount, { emptyValue: '—' })}
                                    </TableCell>
                                    <TableCell>
                                      {formatMinorToMajorAmount(tier?.flat_amount, { emptyValue: '—' })}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2">—</Typography>
                        )}
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type={showInfiniteLimit ? 'text' : 'number'}
                        label="Limit"
                        value={showInfiniteLimit ? 'Unlimited' : itemLimit}
                        error={Boolean(errorMessage)}
                        helperText={limitHelperText}
                        disabled={readOnly || isUsageBased}
                        onChange={(event) => onLimitChange?.(index, event.target.value)}
                        slotProps={{
                          ...(showInfiniteLimit
                            ? {
                                input: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <AllInclusiveIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                    </InputAdornment>
                                  ),
                                },
                              }
                            : {}),
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
      </Stack>
    </Box>
  );
}
